import { Metric } from '@enums';
import type { InsightAttributeCalculations } from '@schema';
import { INSIGHT_MINI_CHART_COLOR_NAMES } from '@utils';
import type { ChartData } from 'types/common';
import type {
  Attribute,
  AttributeCalculations,
  InsightChart
} from 'types/schema';

export type InsightSeriesSuccess = {
  ok: true;
  chartData: ChartData[];
  metric: Metric;
  hasMultipleAudiences: boolean;
  suffixes?: string;
};

export type InsightSeriesFailure = {
  ok: false;
  message: string;
};

export type InsightSeriesResult = InsightSeriesSuccess | InsightSeriesFailure;

export function getDataPointName(
  attribute: Attribute,
  index: number,
  hasMultipleAudiences: boolean,
  suffixes: string | undefined,
  compact: boolean
): string {
  if (hasMultipleAudiences) {
    return `${attribute['datapoint_name']}✂️(${index + 1})`;
  }

  if (compact && suffixes?.split(',').length > 1) {
    return `${attribute['datapoint_name']} - ${attribute['suffix_name']}`;
  }

  return attribute['datapoint_name'];
}

export function isOrigin(
  data: InsightChart,
  attributeCalc: AttributeCalculations,
  hasMultipleAudiences: boolean
): boolean {
  if (hasMultipleAudiences) {
    const audience = data.audiences.find(
      (item) => item.id === attributeCalc['audience_id']
    );

    return Boolean(audience?.['is_origin']);
  }

  return Boolean(attributeCalc.attribute['is_origin']);
}

type MetricProp = keyof Pick<
  InsightAttributeCalculations,
  'percentage' | 'size' | 'index'
>;

const MetricToProp: Record<Metric, MetricProp> = {
  [Metric.AudiencePercentage]: 'percentage',
  [Metric.Universe]: 'size',
  [Metric.Index]: 'index'
};

/**
 * Maps an insight API payload to the same bar/pie series shape used by
 * InsightVisualization (default metric = first in metric_priority).
 */
export function buildChartDataFromInsightChart(
  data: InsightChart,
  metric: Metric = Metric.AudiencePercentage
): InsightSeriesResult {
  if (data.error) {
    return { ok: false, message: data.error.message || 'Insight error' };
  }

  const questionCalculations = data['question_calculations'] ?? [];

  if (questionCalculations.length === 0) {
    return { ok: false, message: 'No chart data for this insight.' };
  }

  const [{ calculations, suffixes }] = questionCalculations;

  if (!calculations?.length) {
    return { ok: false, message: 'No calculations for this insight.' };
  }

  const hasMultipleAudiences = Boolean(
    data.audiences && data.audiences.length > 1
  );

  const memoizedCalculations = hasMultipleAudiences
    ? data.audiences
        .sort(({ title: a }, { title: b }) => (a < b ? -1 : 1))
        .map((audience) =>
          calculations.find((item) => item['audience_id'] === audience.id)
        )
    : calculations;

  const matchedAudiences = memoizedCalculations.map((item) => {
    const index = data.audiences.findIndex(
      (audience) => item?.['audience_id'] === audience.id
    );

    return {
      color:
        INSIGHT_MINI_CHART_COLOR_NAMES[
          index % INSIGHT_MINI_CHART_COLOR_NAMES.length
        ],
      id: data.audiences[index]?.id,
      title: data.audiences[index]?.title
    };
  });

  const metricProp = MetricToProp[metric] ?? MetricToProp[Metric.Index];

  const chartData = memoizedCalculations
    .map(
      (item, index): ChartData => ({
        name: item
          ? getDataPointName(
              item.attribute,
              index,
              hasMultipleAudiences,
              suffixes,
              true
            )
          : '',
        value: item?.[metricProp],
        suffix: item?.attribute['suffix_name'],
        isOrigin: item && isOrigin(data, item, hasMultipleAudiences),
        audience: matchedAudiences?.[index],
        percentage: item?.['percentage'],
        index: item?.['index'],
        size: item?.['size']
      })
    )
    .filter(({ value }) => value !== undefined && value !== null)
    .sort((a, b) => b.value - a.value) satisfies ChartData[];

  if (chartData.length === 0) {
    return { ok: false, message: 'No values to chart for this insight.' };
  }

  return {
    ok: true,
    chartData,
    metric,
    hasMultipleAudiences,
    suffixes
  };
}

/**
 * Slices the chart data for a pie chart, sorting by value and then taking the top maxSlices slices, and then adding an "Other" slice for the rest.
 * @param chartData - The chart data to slice
 * @param maxSlices - The maximum number of slices to return
 * @returns
 */
export function sliceChartData(
  chartData: ChartData[],
  maxSlices: number | undefined
): ChartData[] {
  if (!maxSlices || chartData.length <= maxSlices) {
    return chartData;
  }

  const sorted = chartData.sort((a, b) => b.value - a.value);
  const head = sorted.slice(0, maxSlices - 1);
  const tail = sorted.slice(maxSlices - 1);
  const otherValue = tail.reduce((sum, row) => sum + row.value, 0);
  const otherIndex = tail.reduce(
    (sum, row) => sum + (typeof row.index === 'number' ? row.index : 0),
    0
  );
  const otherSize = tail.reduce((sum, row) => sum + row.size, 0);
  const otherPercentage = tail.reduce((sum, row) => sum + row.percentage, 0);

  return [
    ...head,
    {
      name: 'Other',
      value: otherValue,
      index: otherIndex,
      size: otherSize,
      percentage: otherPercentage
    }
  ];
}
